import { useCallback, useEffect, useMemo, useState } from 'react';
import { archiveProblem } from '../../../models/LibraryModel';

/**
 * What the Library's document menu does (PRD-1606, PRD-1607), and where focus
 * goes after each action, since the control the user came from often changes
 * or leaves the view:
 *
 *   Retry      the trigger goes (an upload has no actions): focus the name
 *   Archive    asks first; Cancel returns to the trigger. Once archived the
 *              item may leave the view: its trigger, else `fallbackRef`
 *   Unarchive  straight away, no dialog: its trigger, else `fallbackRef`
 *   In use     archive refuses with an alert toast: back to its trigger
 *
 * Returns the `actions` the cards and rows hand to their menus, and the
 * document awaiting archive confirmation (with the trigger that opened it).
 */
export function useDocumentActions({ vm, rootRef, fallbackRef, onShowNotification }) {
  const [pendingFocus, setPendingFocus] = useState(null); // { id, part: 'name' | 'menu' }
  const [confirming, setConfirming] = useState(null); // { doc, opener }
  // The view model's callbacks are stable; `vm` itself is a new object per render.
  const { retry, archive, unarchive, findDocument } = vm;

  // Runs after the action's render has committed, so it finds what is on the
  // page now, not what was there when the action started.
  useEffect(() => {
    if (!pendingFocus) return;
    const root = rootRef.current;
    const selector = pendingFocus.part === 'name'
      ? `[data-doc-focus="${pendingFocus.id}"]`
      : `[data-doc-menu="${pendingFocus.id}"] button`;
    const target = root?.querySelector(selector) || fallbackRef.current;
    if (target) target.focus();
    setPendingFocus(null);
  }, [pendingFocus, rootRef, fallbackRef]);

  const actions = useMemo(() => ({
    onRetry: (doc) => {
      retry(doc.id);
      setPendingFocus({ id: doc.id, part: 'name' });
    },
    onArchive: (doc) => {
      const problem = archiveProblem(doc);
      if (problem) {
        onShowNotification?.(problem, 'alert');
        setPendingFocus({ id: doc.id, part: 'menu' });
        return;
      }
      const opener = rootRef.current?.querySelector(`[data-doc-menu="${doc.id}"] button`) || null;
      setConfirming({ doc, opener });
    },
    onUnarchive: (doc) => {
      unarchive(doc.id);
      onShowNotification?.('Document unarchived.', 'success');
      setPendingFocus({ id: doc.id, part: 'menu' });
    },
  }), [retry, unarchive, rootRef, onShowNotification]);

  // If it can no longer be archived (it became in use while the confirm was
  // open), say why rather than closing silently. Focus is LibraryDialog's on
  // close: back to the trigger, else the fallback.
  const confirmArchive = useCallback(() => {
    if (!confirming) return;
    const { doc } = confirming;
    if (archive(doc.id)) onShowNotification?.('Document archived.', 'success');
    else onShowNotification?.(archiveProblem(findDocument(doc.id) || doc) || 'This document can\'t be archived right now.', 'alert');
    setConfirming(null);
  }, [confirming, archive, findDocument, onShowNotification]);

  // Stable, so the confirm's Escape listener isn't re-added on every upload tick.
  const cancelArchive = useCallback(() => setConfirming(null), []);

  return { actions, confirming, confirmArchive, cancelArchive };
}
